import React from 'react';
import PropTypes from 'prop-types';
import { Outlet } from 'react-router-dom';
import { useAuth } from '../../../contexts/AuthContext';
import Sidebar from '../Sidebar';
import Header from '../Header';
import styles from './DashboardLayout.module.css';
import FileService from '../../../services/file.service';
import OperationalBanner from '../../practiceAdmin/OperationalBanner';

// Import logo
import logoWhite from '../../../assets/logos/logo-white.png';

// Import icons
import analyticsIcon from '../../../assets/icons/analytics-icon.svg';
import healthCareProvidersIcon from '../../../assets/icons/health-care-providers-icon.svg';
import patientsIcon from '../../../assets/icons/patients-icon.svg';
import settingsIcon from '../../../assets/icons/settings-icon.svg';
import consultationsIcon from '../../../assets/icons/consultations-icon.svg';
import medicalRecordsIcon from '../../../assets/icons/medical-records-icon.svg';
import immunizationsIcon from '../../../assets/icons/immunizations-icon.svg';
import hospitalIcon from '../../../assets/icons/hospital-icon.svg';
import surgeryIcon from '../../../assets/icons/surgery-icon.svg';
import biometricsIcon from '../../../assets/icons/vitals-icon.svg';
import connectionsIcon from '../../../assets/icons/connections-icon.svg';
import profileIcon from '../../../assets/icons/profile-icon.svg';
import signOutIcon from '../../../assets/icons/sign-out-icon.svg';
import helpIcon from '../../../assets/icons/help-icon.svg';

// Navigation items for different roles
const getNavItems = (role, logoutFn) => {
  const baseItems = {
    admin: [
      { to: '/admin/dashboard', label: 'Dashboard', icon: <img src={analyticsIcon} alt="Dashboard" /> },
      { to: '/admin/health-providers', label: 'Health Care Providers', icon: <img src={healthCareProvidersIcon} alt="Health Providers" /> },
      { to: '/admin/patients', label: 'Patients', icon: <img src={patientsIcon} alt="Patients" /> },
      { to: '/admin/audit-logs', label: 'Audit Trail', icon: <img src={medicalRecordsIcon} alt="Audit Trail" /> },
      { to: '/admin/consent-report', label: 'Consent Report', icon: <img src={medicalRecordsIcon} alt="Consent Report" /> },
      { to: '/admin/settings', label: 'Settings', icon: <img src={settingsIcon} alt="Settings" /> },
    ],
    provider: [
      { to: '/provider/dashboard', label: 'Dashboard', icon: <img src={analyticsIcon} alt="Dashboard" /> },
      { to: '/provider/patients', label: 'Patients', icon: <img src={patientsIcon} alt="Patients" /> },
      { to: '/provider/consultations', label: 'Consultations', icon: <img src={consultationsIcon} alt="Consultations" /> },
      { to: '/provider/immunizations', label: 'Immunizations', icon: <img src={immunizationsIcon} alt="Immunizations" /> },
      { to: '/provider/hospital-admissions', label: 'Hospital', icon: <img src={hospitalIcon} alt="Hospital" /> },
      { to: '/provider/surgeries', label: 'Surgeries', icon: <img src={surgeryIcon} alt="Surgeries" /> },
      { to: '/provider/biometrics', label: 'Biometrics', icon: <img src={biometricsIcon} alt="Biometrics" /> },
      { to: '/provider/billing', label: 'Billing', icon: <img src={medicalRecordsIcon} alt="Billing" /> },
      { to: '/provider/team', label: 'Team', icon: <img src={connectionsIcon} alt="Team" /> },
      { to: '/provider/profile', label: 'Profile', icon: <img src={profileIcon} alt="Profile" /> },
      { to: '/provider/settings', label: 'Settings', icon: <img src={settingsIcon} alt="Settings" /> },
    ],
    practice_admin: [
      { to: '/practice-admin/patients', label: 'Patients', icon: <img src={patientsIcon} alt="Patients" /> },
      { to: '/practice-admin/billing', label: 'Billing', icon: <img src={medicalRecordsIcon} alt="Billing" /> },
      { to: '/practice-admin/profile', label: 'Profile', icon: <img src={profileIcon} alt="Profile" /> },
      { to: '/practice-admin/settings', label: 'Settings', icon: <img src={settingsIcon} alt="Settings" /> },
    ],
    patient: [
      { to: '/patient/dashboard', label: 'Dashboard', icon: <img src={analyticsIcon} alt="Dashboard" /> },
      { to: '/patient/consultations', label: 'Consultations', icon: <img src={consultationsIcon} alt="Consultations" /> },
      { to: '/patient/medical-records', label: 'Medical Records', icon: <img src={medicalRecordsIcon} alt="Medical Records" /> },
      { to: '/patient/hospital-admissions', label: 'Hospital Admissions', icon: <img src={hospitalIcon} alt="Hospital Admissions" /> },
      { to: '/patient/surgeries', label: 'Surgeries', icon: <img src={surgeryIcon} alt="Surgeries" /> },
      { to: '/patient/connections', label: 'Connections', icon: <img src={connectionsIcon} alt="Connections" /> },
      { to: '/patient/profile', label: 'Profile', icon: <img src={profileIcon} alt="Profile" /> },
      { to: '/patient/settings', label: 'Settings', icon: <img src={settingsIcon} alt="Settings" /> },
    ]
  };

  // /admin/help, /provider/help, /patient/help, /practice-admin/help
  const helpPath = role === 'practice_admin' ? '/practice-admin/help' : `/${role}/help`;

  const footerItems = [
    { label: 'Sign Out', icon: <img src={signOutIcon} alt="Sign Out" />, onClick: logoutFn },
    { to: helpPath, label: 'Help', icon: <img src={helpIcon} alt="Help" /> },
  ];

  return {
    items: baseItems[role] || [],
    footerItems
  };
};

/**
 * Layout for authenticated dashboard pages with role-specific navigation
 */
const DashboardLayout = ({ role }) => {
  const { user, logout } = useAuth();
  const { items, footerItems } = getNavItems(role, logout);
  
  // Create the logo component
  const logo = (
    <div className={styles.logoContainer}>
      <img src={logoWhite} alt="Onus Logo" className={styles.logoImage} />
    </div>
  );

  // Get user information based on role
  const getUserDisplay = () => {
    const profileUrlMap = {
      admin: '/admin/settings',
      provider: '/provider/profile',
      patient: '/patient/profile',
      practice_admin: '/practice-admin/profile'
    };

    // Try to get name from user profile data
    let fullName = '';
    let firstName = '';
    let lastName = '';
    
    // Check for personalInfo in user profile (Patient)
    if (user?.profile?.personalInfo) {
      firstName = user.profile.personalInfo.firstName || '';
      lastName = user.profile.personalInfo.lastName || '';
      if (firstName && lastName) {
        fullName = `${firstName} ${lastName}`;
      }
    } 
    // Check for professionalInfo in user profile (Provider)
    else if (user?.profile?.professionalInfo) {
      firstName = user.profile.professionalInfo.firstName || '';
      lastName = user.profile.professionalInfo.lastName || '';
      if (firstName && lastName) {
        fullName = `${firstName} ${lastName}`;
      }
    }
    // Fallback to firstName/lastName directly on user object
    else if (user?.firstName && user?.lastName) {
      firstName = user.firstName;
      lastName = user.lastName;
      fullName = `${firstName} ${lastName}`;
    }
    // Last resort: use name property if available
    else if (user?.name) {
      fullName = user.name;
      // Try to parse first and last name from full name
      const nameParts = user.name.trim().split(' ');
      if (nameParts.length >= 2) {
        firstName = nameParts[0];
        lastName = nameParts[nameParts.length - 1];
      } else {
        firstName = nameParts[0] || '';
        lastName = '';
      }
    }
    
    // Add Dr. prefix for providers if we have a name
    let prefix = (role === 'provider' && fullName) ? 'Dr. ' : '';
    
    // Use an empty string if no name is available
    const displayName = fullName ? prefix + fullName : '';
    
    const roleLabelMap = {
      provider: 'Doctor',
      practice_admin: 'Practice Admin',
      admin: 'Admin',
      patient: 'Patient'
    };

    return {
      name: displayName,
      firstName: firstName,
      lastName: lastName,
      profileUrl: profileUrlMap[role] || '#',
      roleLabel: roleLabelMap[role] || null,
      avatarUrl: user?.profileImage ? FileService.getProfilePictureUrl(user.profileImage, user?._id || user?.id, true) : null
    };
  };

  return (
    <div className={styles.dashboardLayout}>
      <Sidebar 
        logo={logo}
        items={items}
        footerItems={footerItems}
        className={styles.sidebar}
      />
      <div className={styles.mainContent}>
        <Header 
          user={getUserDisplay()}
          logo={logo}
          navigationItems={items}
          navigationFooterItems={footerItems}
          className={styles.header}
        />
        <main className={styles.content}>
          {role === 'practice_admin' && <OperationalBanner />}
          <Outlet />
        </main>
      </div>
    </div>
  );
};

DashboardLayout.propTypes = {
  role: PropTypes.oneOf(['patient', 'provider', 'admin', 'practice_admin']).isRequired,
};

export default DashboardLayout; 